import { FinaleClock, resultCardVisible } from '../game/finale';
import { CATALOGUE, type LocalSession } from '../game/session';
import { scoreSaveCopy } from '../online/service';
import type { Camp } from '@orbomb/sim';

export function countdownCopy(
  remainingTicks: number,
  waitingForPeer: boolean,
): { clock: string; phase: string; banner: string } {
  if (waitingForPeer) {
    return { clock: '…', phase: 'GET READY', banner: 'GET READY' };
  }
  const n = Math.ceil(remainingTicks / 60);
  const label = n > 0 ? String(n) : 'GO';
  return { clock: label, phase: 'COUNTDOWN', banner: label };
}

/** A lone player sees their own outcome; two local players or two bots see the winning camp. */
export function resultHeadline(winner: Camp, playerCamp: Camp | null): string {
  if (playerCamp === null) return `${winner} WINS`;
  return winner === playerCamp ? 'YOU WIN' : 'YOU LOSE';
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

export class Hud {
  private readonly clock = document.getElementById('clock') as HTMLElement;
  private readonly phase = document.getElementById('phase') as HTMLElement;
  private readonly banner = document.getElementById('banner') as HTMLElement;
  private readonly resultOverlay = document.getElementById('result-overlay') as HTMLElement;
  private readonly resultWinner = document.getElementById('result-winner') as HTMLElement;
  private readonly resultScore = document.getElementById('result-score') as HTMLElement;
  private readonly debug = document.getElementById('debug') as HTMLElement;
  private readonly debugState = document.getElementById('debug-state') as HTMLElement;
  private readonly debugLog = document.getElementById('debug-log') as HTMLElement;
  private readonly seedInput = document.getElementById('seed-input') as HTMLInputElement;
  private readonly scenarioSelect = document.getElementById('scenario-select') as HTMLSelectElement;
  private readonly finale = new FinaleClock();

  constructor(private readonly session: LocalSession) {
    for (const scenario of CATALOGUE) {
      const option = document.createElement('option');
      option.value = scenario.id;
      option.textContent = `${scenario.number}. ${scenario.title}`;
      this.scenarioSelect.append(option);
    }
  }

  bind(handlers: {
    onSeed: (seed: number) => void;
    onStep: () => void;
    onSnapshot: () => void;
    onScenario: (id: string) => void;
    onRematch: () => void;
    onDeclineRematch: () => void;
  }): void {
    document.getElementById('btn-apply-seed')?.addEventListener('click', () => {
      handlers.onSeed(Number(this.seedInput.value) || 1);
    });
    document.getElementById('btn-step')?.addEventListener('click', () => handlers.onStep());
    document.getElementById('btn-snapshot')?.addEventListener('click', () => handlers.onSnapshot());
    document.getElementById('btn-rematch')?.addEventListener('click', () => handlers.onRematch());
    document
      .getElementById('btn-no-rematch')
      ?.addEventListener('click', () => handlers.onDeclineRematch());
    this.scenarioSelect.addEventListener('change', () => {
      if (this.scenarioSelect.value) handlers.onScenario(this.scenarioSelect.value);
    });
  }

  /** True once the match-over card is on screen. Rematch keys wait for this. */
  get showingResult(): boolean {
    return !this.resultOverlay.hidden;
  }

  sync(now = performance.now()): void {
    const { state } = this.session;
    const ended = state.clock.phase === 'ENDED' && state.result !== null;
    const elapsed = this.finale.elapsed(
      ended ? state.result?.cause : null,
      ended ? (state.result?.tick ?? null) : null,
      now,
    );
    this.resultOverlay.hidden = !(ended && resultCardVisible(state.result?.cause, elapsed));

    if (state.clock.phase === 'COUNTDOWN') {
      const copy = countdownCopy(
        this.session.countdownTicks(),
        this.session.waitingForPeer && state.tick === 0,
      );
      this.clock.textContent = copy.clock;
      this.phase.textContent = copy.phase;
      this.showBanner(copy.banner);
    } else if (state.clock.phase === 'OVERTIME') {
      this.clock.textContent = '0:00';
      this.phase.textContent = 'OVERTIME';
      this.hideBanner();
    } else if (state.clock.phase === 'SUDDEN_DEATH') {
      this.clock.textContent = 'SD';
      this.phase.textContent = 'SUDDEN DEATH';
      this.hideBanner();
    } else if (state.clock.phase === 'ENDED' && state.result) {
      this.clock.textContent = 'END';
      this.phase.textContent = state.result.cause.replaceAll('_', ' ');
      this.hideBanner();
      this.resultWinner.textContent = resultHeadline(state.result.winner, this.session.playerCamp);
      this.resultScore.textContent = `${state.totems[0].hp} — ${state.totems[1].hp} lives`;
      const ask = this.resultOverlay.querySelector('.result-ask');
      const actions = this.resultOverlay.querySelector('.result-actions');
      const online = this.session.onlineCamp !== null;
      if (ask) {
        ask.textContent = this.session.rematchDeclined
          ? 'See you next match.'
          : online
            ? `${scoreSaveCopy(this.session.resultNote)} Play again?`
            : 'Play again?';
      }
      if (actions instanceof HTMLElement) actions.hidden = this.session.rematchDeclined;
      const hint = document.getElementById('result-hint');
      if (hint) {
        hint.textContent = online
          ? 'Y / Enter new room · N leave · Home leaves'
          : 'Y / Enter yes · N no · R restart';
      }
    } else {
      this.clock.textContent = formatClock(Math.ceil(this.session.remainingClockTicks() / 60));
      this.phase.textContent = 'REGULATION';
      this.hideBanner();
    }

    this.debug.hidden = !this.session.showDebug;
    this.seedInput.value = String(this.session.seed);
    if (this.session.showDebug) {
      const p0 = state.players[0];
      const p1 = state.players[1];
      this.debugState.textContent = [
        `tick ${state.tick}  phase ${state.clock.phase}`,
        `seed ${state.seed}  room ${this.session.roomKind ?? (this.session.onlineCamp ? `online ${this.session.onlineCamp}` : '—')}  bots O${this.session.botOrange} T${this.session.botTurquoise}`,
        `O ${p0.x.toFixed(2)},${p0.y.toFixed(2)} v ${p0.vx.toFixed(2)},${p0.vy.toFixed(2)} ${p0.status}`,
        `T ${p1.x.toFixed(2)},${p1.y.toFixed(2)} v ${p1.vx.toFixed(2)},${p1.vy.toFixed(2)} ${p1.status}`,
        `lives O${state.totems[0].hp} T${state.totems[1].hp}`,
        `goldens ${state.goldens.map((g) => g.status[0]).join(' ')}`,
        `explosions ${state.explosions.length}`,
        ...(this.session.debugNet ? [this.session.debugNet, 'F2 cycles net A/B/C'] : []),
      ].join('\n');
      this.debugLog.textContent = this.session.log
        .slice(-12)
        .map((e) => `${e.tick}.${e.seq} ${e.type}`)
        .join('\n');
    }
  }

  private showBanner(text: string): void {
    const changed = this.banner.hidden || this.banner.textContent !== text;
    this.banner.hidden = false;
    if (!changed) return;
    this.banner.textContent = text;
    // Replay the pop on every new count (3 → 2 → 1 → GO).
    this.banner.classList.remove('pop');
    void this.banner.offsetWidth;
    this.banner.classList.add('pop');
  }

  private hideBanner(): void {
    this.banner.hidden = true;
  }
}
